/**
 * CQG credentials are EXCLUSIVELY FE-owned (2026-07-08 directive: they are FORBIDDEN from
 * transiting our BE). Stashed in localStorage on connect; the relay reads them at every
 * socket-open to build + send the CQG Logon DIRECTLY over the browser's CQG WebSocket — the only
 * wire they ever ride. /cqg/connect is a credential-less body-less POST. NOTE: localStorage is
 * XSS-readable — accepted trade-off (user decision 2026-07-08: survives crash/restart, prefilled
 * reconnect); revisit with a more secure store if requirements change.
 *
 * Extracted from CqgConnectorDialog so the Account Sync onboarding page can stash creds without
 * pulling the whole dialog/relay bundle into the pre-auth chunk.
 */
export const CREDS_STORAGE_KEY = 'cqg.credentials.v1';

/**
 * Which CQG gateway pair the user connects to (2026-07-18 Demo/Live selector). Rides the connect POST
 * as `{ environment }` (non-secret — it only picks the gateway/app-id pair, mirroring Rithmic's
 * `system`); the BE resolves the URL + logon params and pushes them via CqgOpenSocket. Persisted with
 * the creds; existing stored creds (no field) default to 'live' (localhost preference: real CQG).
 */
export type CqgEnvironment = 'demo' | 'live';

export function readStoredCqgCreds(): { username: string; password: string; environment: CqgEnvironment } {
  try {
    const raw = localStorage.getItem(CREDS_STORAGE_KEY);
    if (!raw) return { username: '', password: '', environment: 'live' };
    const p = JSON.parse(raw) as { username?: string; password?: string; environment?: string };
    return {
      username: p.username ?? '',
      password: p.password ?? '',
      environment: p.environment === 'demo' ? 'demo' : 'live',
    };
  } catch { return { username: '', password: '', environment: 'live' }; }
}

export function storeCqgCreds(username: string, password: string, environment?: CqgEnvironment): void {
  try {
    // Callers that don't choose an environment (legacy paths) keep whatever is already stored.
    const env = environment ?? readStoredCqgCreds().environment;
    localStorage.setItem(CREDS_STORAGE_KEY, JSON.stringify({ username, password, environment: env }));
  } catch { /* storage unavailable (private mode etc) — non-fatal */ }
}

export function clearStoredCqgCreds(): void {
  try { localStorage.removeItem(CREDS_STORAGE_KEY); } catch { /* non-fatal */ }
}

// ── "Concurrent login is being set up" one-time notice ──────────────────────────────────────────
// After we detect that a user's CQG login can't run the journal + their trading platform at once (a
// LIVE concurrent_session conflict — surfaced by CqgReconnectPopup's "Not now" or the dialog take-over
// "Cancel") and submit the concurrent-login request, the app-wide CqgConnectedPopup shows the 48h
// explainer ONCE. Persist (localStorage, cross-session) that it has been shown so it never re-nags;
// the dialog's own inline notice (ConcurrentNoticeView) also marks it seen so the two never both fire.
const CONNECTED_POPUP_SEEN_KEY = 'cqg.connectedPopup.seen';

export function hasSeenConnectedPopup(): boolean {
  try { return localStorage.getItem(CONNECTED_POPUP_SEEN_KEY) === '1'; } catch { return false; }
}

export function markConnectedPopupSeen(): void {
  try { localStorage.setItem(CONNECTED_POPUP_SEEN_KEY, '1'); } catch { /* non-fatal */ }
}
